import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { BookOpenText, Scale, LineChart } from "lucide-react";

export default function AccountingPage() {
  const { token } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [journal, setJournal] = useState([]);
  const [balancete, setBalancete] = useState(null);
  const [dre, setDre] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const q = `?start=${start}&end=${end}`;
      const [jRes, bRes, dRes] = await Promise.all([
        axios.get(`${API_URL}/accounting/journal${q}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/accounting/balancete${q}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/accounting/dre${q}`, { headers, withCredentials: true }),
      ]);
      setJournal(jRes.data.entries);
      setBalancete(bRes.data);
      setDre(dRes.data);
    } catch { toast.error("Erro ao carregar contabilidade"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Contabilidade</h1>
          <p className="text-muted-foreground">Lançamentos automáticos (PGC-AO simplificado), balancete e demonstração de resultados</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="acc-start-input" />
          <span className="text-muted-foreground">a</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="acc-end-input" />
        </div>
      </div>

      <Tabs defaultValue="dre">
        <TabsList>
          <TabsTrigger value="dre" data-testid="tab-dre"><LineChart className="w-4 h-4 mr-1" /> Demonstração de Resultados</TabsTrigger>
          <TabsTrigger value="balancete" data-testid="tab-balancete"><Scale className="w-4 h-4 mr-1" /> Balancete</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal"><BookOpenText className="w-4 h-4 mr-1" /> Diário</TabsTrigger>
        </TabsList>

        <TabsContent value="dre" className="mt-4">
          {dre && (
            <Card>
              <CardContent className="p-6 max-w-lg space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-2">Receitas</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Vendas</span><span className="font-mono">{formatCurrency(dre.receitas.vendas)}</span></div>
                    <div className="flex justify-between"><span>Outros Proveitos</span><span className="font-mono">{formatCurrency(dre.receitas.outros_proveitos)}</span></div>
                    <div className="flex justify-between font-bold border-t pt-1"><span>Total Receitas</span><span className="font-mono">{formatCurrency(dre.receitas.total)}</span></div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-2">Custos</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Compras / Mercadorias</span><span className="font-mono">{formatCurrency(dre.custos.compras_mercadorias)}</span></div>
                    <div className="flex justify-between"><span>Fornecimentos e Serviços Externos</span><span className="font-mono">{formatCurrency(dre.custos.fse)}</span></div>
                    <div className="flex justify-between"><span>Custos com Pessoal</span><span className="font-mono">{formatCurrency(dre.custos.pessoal)}</span></div>
                    <div className="flex justify-between font-bold border-t pt-1"><span>Total Custos</span><span className="font-mono">{formatCurrency(dre.custos.total)}</span></div>
                  </div>
                </div>
                <div className={`flex justify-between text-lg font-bold border-t-2 pt-3 ${dre.resultado >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  <span>Resultado do Período</span>
                  <span className="font-mono" data-testid="dre-resultado">{formatCurrency(dre.resultado)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balancete" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-12 gap-2 p-3 text-xs font-bold uppercase text-muted-foreground border-b">
                <span className="col-span-1">Conta</span>
                <span className="col-span-5">Descrição</span>
                <span className="col-span-2 text-right">Débito</span>
                <span className="col-span-2 text-right">Crédito</span>
                <span className="col-span-2 text-right">Saldo</span>
              </div>
              {(balancete?.accounts || []).map((a) => (
                <div key={a.account} className="grid grid-cols-12 gap-2 p-3 text-sm border-b" data-testid={`balancete-row-${a.account}`}>
                  <span className="col-span-1 font-mono">{a.account}</span>
                  <span className="col-span-5">{a.account_name}</span>
                  <span className="col-span-2 text-right font-mono">{formatCurrency(a.debit)}</span>
                  <span className="col-span-2 text-right font-mono">{formatCurrency(a.credit)}</span>
                  <span className={`col-span-2 text-right font-mono font-bold ${a.balance >= 0 ? "" : "text-red-600"}`}>{formatCurrency(a.balance)}</span>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-2 p-3 text-sm font-bold">
                <span className="col-span-6">TOTAIS</span>
                <span className="col-span-2 text-right font-mono">{formatCurrency(balancete?.total_debit)}</span>
                <span className="col-span-2 text-right font-mono">{formatCurrency(balancete?.total_credit)}</span>
                <span className="col-span-2"></span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          <Card>
            <CardContent className="p-0 divide-y max-h-[600px] overflow-y-auto">
              {loading ? <p className="p-6 text-muted-foreground">A carregar...</p> :
                journal.length === 0 ? <p className="p-6 text-center text-muted-foreground">Sem lançamentos no período</p> :
                journal.map((e, i) => (
                  <div key={i} className="p-3 text-sm">
                    <div className="flex justify-between">
                      <p className="font-medium">{e.description}</p>
                      <p className="text-xs text-muted-foreground">{e.date} · <span className="font-mono">{e.doc}</span></p>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {e.lines.map((l, li) => (
                        <div key={li} className="grid grid-cols-12 gap-2 text-xs text-muted-foreground">
                          <span className="col-span-6 pl-4">{l.account} — {l.account_name}</span>
                          <span className="col-span-3 text-right font-mono">{l.debit ? formatCurrency(l.debit) : ""}</span>
                          <span className="col-span-3 text-right font-mono">{l.credit ? formatCurrency(l.credit) : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
